import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Typography, Box, Divider, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
  IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Switch,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import CategoryIcon from '@material-ui/icons/Category';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import {
  renderChipList, getWarehouseName, getCountryName, RULES_PER_PAGE, normalizeSlaRule,
} from './shared/helpers';
import { useSuppliers } from './shared/supplierApiHelpers';
import { useProductFilterMaster } from './shared/productFilterApiHelpers';
import {
  buildCategoryLabelMap,
  optionsToLabelMap,
  mapIdsToLabels,
  resolveCityLabelsForRules,
  slaRulesCityResolutionKey,
} from './shared/slaDisplayResolvers';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    color: '#3f51b5',
    fontSize: 24,
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 400,
    color: '#333',
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'flex-end',
  },
  filterControl: {
    minWidth: 180,
  },
  refreshButton: {
    backgroundColor: '#e0e0e0',
    color: '#666',
    '&:hover': { backgroundColor: '#d5d5d5' },
  },
  tableContainer: {
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    borderRadius: theme.spacing(1),
  },
  tableHead: {
    backgroundColor: '#fafafa',
  },
  tableHeadCell: {
    fontWeight: 600,
    color: '#333',
    fontSize: '12px',
    borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  tableCell: {
    padding: theme.spacing(1, 1.5),
    fontSize: '12px',
    color: '#333',
    maxWidth: 160,
  },
  slaBadge: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontWeight: 600,
    fontSize: '12px',
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    display: 'inline-block',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: '#999',
  },
  emptyIcon: {
    fontSize: 48,
    color: '#ddd',
    marginBottom: theme.spacing(1),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  loadingOverlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
}));

const AttributeRulesTable = ({
  rules,
  loading,
  warehouses,
  countries: countryList,
  statusFilter,
  warehouseFilter,
  onFiltersChange,
  onDelete,
  onRefresh,
  onToggleStatus,
  onEdit,
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cityLabelById, setCityLabelById] = useState({});
  const { suppliers } = useSuppliers();
  const {
    soldBy: soldByOptions,
    brandName: brandOptions,
    category: categoryTree,
  } = useProductFilterMaster();

  useEffect(() => {
    setPage(1);
  }, [statusFilter, warehouseFilter]);

  const pagedRules = rules.slice((page - 1) * RULES_PER_PAGE, page * RULES_PER_PAGE);
  const resolveCountries = (codes) => (codes || []).map((c) => getCountryName(c, countryList));

  const labelMaps = useMemo(
    () => ({
      category: buildCategoryLabelMap(categoryTree),
      soldBy: optionsToLabelMap(soldByOptions),
      brand: optionsToLabelMap(brandOptions),
      seller: optionsToLabelMap(suppliers),
    }),
    [categoryTree, soldByOptions, brandOptions, suppliers]
  );

  const cityResolutionKey = useMemo(() => slaRulesCityResolutionKey(rules), [rules]);

  useEffect(() => {
    let cancelled = false;
    resolveCityLabelsForRules(rules, {
      getCountryName: (code) => getCountryName(code, countryList),
    })
      .then((m) => {
        if (!cancelled) setCityLabelById(m || {});
      })
      .catch(() => {
        if (!cancelled) setCityLabelById({});
      });
    return () => { cancelled = true; };
    // rules omitted on purpose: cityResolutionKey tracks geo fields; avoids refetch when `rules` array identity flips without data change
  }, [cityResolutionKey, countryList]);

  const confirmDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.sectionHeader}>
        <Box className={classes.sectionHeaderLeft}>
          <CategoryIcon className={classes.sectionIcon} />
          <Typography className={classes.sectionTitle}>SLA rules ({rules.length})</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton className={classes.refreshButton} onClick={onRefresh} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.filtersRow}>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => onFiltersChange({ statusFilter: e.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Warehouse</InputLabel>
          <Select
            label="Warehouse"
            value={warehouseFilter}
            onChange={(e) => onFiltersChange({ warehouseFilter: e.target.value })}
          >
            <MenuItem value=""><em>All warehouses</em></MenuItem>
            {warehouses.map((wh) => (
              <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                {wh.warehouse_name || wh.warehouse_id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {loading ? (
        <Box className={classes.loadingOverlay}><CircularProgress /></Box>
      ) : rules.length === 0 ? (
        <Box className={classes.emptyState}>
          <CategoryIcon className={classes.emptyIcon} />
          <Typography variant="body1" color="textSecondary">No SLA rules match the current filters</Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>#</TableCell>
                  <TableCell className={classes.tableHeadCell}>Name</TableCell>
                  <TableCell className={classes.tableHeadCell}>Warehouse</TableCell>
                  <TableCell className={classes.tableHeadCell}>Countries</TableCell>
                  <TableCell className={classes.tableHeadCell}>Cities</TableCell>
                  <TableCell className={classes.tableHeadCell}>Categories</TableCell>
                  <TableCell className={classes.tableHeadCell}>Sold by</TableCell>
                  <TableCell className={classes.tableHeadCell}>Sellers</TableCell>
                  <TableCell className={classes.tableHeadCell}>Brands</TableCell>
                  <TableCell className={classes.tableHeadCell}>+SLA</TableCell>
                  <TableCell className={classes.tableHeadCell}>Pri.</TableCell>
                  <TableCell className={classes.tableHeadCell}>Active</TableCell>
                  <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRules.map((raw, idx) => {
                  const rule = normalizeSlaRule(raw);
                  return (
                    <TableRow key={rule._id || idx} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>{(page - 1) * RULES_PER_PAGE + idx + 1}</TableCell>
                      <TableCell className={classes.tableCell}>{rule.rule_name || '—'}</TableCell>
                      <TableCell className={classes.tableCell}>{getWarehouseName(rule.last_mile_warehouse, warehouses)}</TableCell>
                      <TableCell className={classes.tableCell}>{renderChipList(resolveCountries(rule.destination_countries))}</TableCell>
                      <TableCell className={classes.tableCell}>
                        {renderChipList(mapIdsToLabels(rule.destination_cities, cityLabelById))}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {renderChipList(mapIdsToLabels(rule.category_ids, labelMaps.category))}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {renderChipList(mapIdsToLabels(rule.sold_by, labelMaps.soldBy))}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {renderChipList(mapIdsToLabels(rule.seller_ids, labelMaps.seller))}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {renderChipList(mapIdsToLabels(rule.brand_names, labelMaps.brand))}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <span className={classes.slaBadge}>+{rule.additional_sla}d</span>
                      </TableCell>
                      <TableCell className={classes.tableCell}>{rule.priority}</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Switch
                          size="small"
                          checked={rule.status}
                          onChange={(e) => onToggleStatus(rule._id, e.target.checked)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onEdit(raw)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(rule._id)} style={{ color: '#f44336' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {rules.length > RULES_PER_PAGE && (
            <Box className={classes.paginationContainer}>
              <Pagination
                count={Math.ceil(rules.length / RULES_PER_PAGE)}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete SLA rule?</DialogTitle>
        <DialogContent>
          <DialogContentText>This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="default">Cancel</Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AttributeRulesTable;
